import { useCallback, useMemo, useEffect, memo } from 'react';
import { TVariantCaseQuery } from '../../../hooks/use-variantcase-qs';
import { casesStore, useCasesActions, IFilterCase } from '../../../stores/cases.store';
import { useShallow } from 'zustand/react/shallow';
import { useGetListLabelCaseQuery } from '@/features/cases/label-case/api/get-list-label-case.api';
import { useGetListAgentQuery } from '@/features/users/agent/api/get-list-agent-api';
import { useGetListTeamQuery } from '@/features/users/team/api/get-list-team-api';
import {
  useOrganizationChannel,
  TOrganizationChannel,
} from '@/features/channels/_shared/api/get-organization-channel.api';
import { TLabelCase } from '@/features/cases/label-case/types/label-case.type';
import { TAgent } from '@/features/users/agent/types/agent.type';
import { TTeam } from '@/features/users/team/types/team.type';
import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import { handleDomBubbleEvent } from '@/utils/dom';
import GenericMultiselectDropdown from '@/components/ui/multiselect-dropdown/generic-multiselect-dropdown';
import { MultiSelectDropdown } from '@/components/_ui/multiselect';
import Avatar from '@/components/ui/avatar';
import { TChannelOption } from '@/features/channels/_shared/types/channel.type';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalCloseButton,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { Input } from '@/components/_ui/input';

export type TFilterCaseModalType = 'FilterCaseModal';
export interface IFilterCaseModalProps {
  variant: TVariantCaseQuery;
}

const FilterCaseModal = memo(({ variant }: IFilterCaseModalProps) => {
  const casesActions = useCasesActions();
  const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();

  // Get pending filter state based on variant
  const pendingFilter = casesStore(
    useShallow((state) => {
      if (variant === 'activeCase') return state.pendingFilterActiveCase;
      if (variant === 'unassignCase') return state.pendingFilterUnassignCase;
      if (variant === 'resolvedCase') return state.pendingFilterResolvedCase;
      return state.pendingFilterActiveCase; // default
    })
  );

  // Get current active filter state based on variant (the applied filter, not pending)
  const currentFilter = casesStore(
    useShallow((state) => {
      if (variant === 'activeCase') return state.filterActiveCase;
      if (variant === 'unassignCase') return state.filterUnassignCase;
      if (variant === 'resolvedCase') return state.filterResolvedCase;
      return state.filterActiveCase; // default
    })
  );

  // Check if modal is open
  const isOpen = useMemo(() => {
    return caseModuleModalRootActions.checkIsOpenModal('FilterCaseModal');
  }, [caseModuleModalRootActions]);

  // Initialize pending filter when modal opens - sync from active filter
  useEffect(() => {
    if (isOpen) {
      // Sync pending filter from active filter when modal opens
      // This ensures pending filter always starts with the current applied filter
      if (variant === 'activeCase') {
        casesActions.setPendingFilterActiveCase(currentFilter);
      } else if (variant === 'unassignCase') {
        casesActions.setPendingFilterUnassignCase(currentFilter);
      } else if (variant === 'resolvedCase') {
        casesActions.setPendingFilterResolvedCase(currentFilter);
      }
    }
  }, [isOpen, variant, currentFilter, casesActions]);

  const filterModalTitle = useMemo(() => {
    if (variant === 'activeCase') {
      return 'Filter Active Case';
    } else if (variant === 'unassignCase') {
      return 'Filter Unassign Case';
    } else if (variant === 'resolvedCase') {
      return 'Filter Resolved Case';
    }
    return 'Filter Case';
  }, [variant]);

  /**
   * Label Case Filter
   */
  const { data: labelCase, isLoading: isLoadingLabelCase } = useGetListLabelCaseQuery({
    params: {
      search: '',
      page: 1,
      limit: 500,
      sortDir: 'DESC',
      sortBy: 'name',
    },
  });

  const labelCaseOptions = useMemo(() => {
    return (labelCase?.payload?.rows || []) as TLabelCase[];
  }, [labelCase]);

  /**
   * Agent Filter
   */
  const { data: agentData, isLoading: isLoadingAgent } = useGetListAgentQuery({
    params: {
      page: 1,
      limit: 500,
      sortDir: 'DESC',
      sortBy: 'fullname',
      search: '',
    },
  });

  const agentOptions = useMemo(() => {
    return (agentData?.payload?.rows || []) as TAgent[];
  }, [agentData]);

  /**
   * Team Filter
   */
  const { data: teamData, isLoading: isLoadingTeam } = useGetListTeamQuery({
    params: {
      page: 1,
      limit: 500,
      sortDir: 'DESC',
      sortBy: 'name',
      search: '',
    },
  });

  const teamOptions = useMemo(() => {
    return (teamData?.payload?.rows || []) as TTeam[];
  }, [teamData]);

  /**
   * Organization Channel
   */
  const { data: organizationChannelData, isLoading: isLoadingOrganizationChannel } = useOrganizationChannel({
    params: {
      page: 1,
      limit: 500,
      sortDir: 'DESC',
      sortBy: 'createdAt',
    },
  });

  const organizationChannelOptions = useMemo(() => {
    return (organizationChannelData?.payload?.rows || []) as unknown as TOrganizationChannel[];
  }, [organizationChannelData]);

  /**
   * Selected values from pending filter
   */
  const selectedLabelCases = useMemo(() => {
    return pendingFilter.labelCases || [];
  }, [pendingFilter.labelCases]);

  const selectedChannels = useMemo(() => {
    return pendingFilter.channels || [];
  }, [pendingFilter.channels]);

  const selectedAgents = useMemo(() => {
    return pendingFilter.agents || [];
  }, [pendingFilter.agents]);

  const selectedTeams = useMemo(() => {
    return pendingFilter.teams || [];
  }, [pendingFilter.teams]);

  const searchCustomerOrGroupName = useMemo(() => {
    return pendingFilter.searchCustomerOrGroupName || '';
  }, [pendingFilter.searchCustomerOrGroupName]);

  /**
   * Handle filter changes - update pending filter
   */
  const handleFilterChange = useCallback(
    (filter: Partial<IFilterCase>) => {
      const updatedFilter = { ...pendingFilter, ...filter };
      if (variant === 'activeCase') {
        casesActions.setPendingFilterActiveCase(updatedFilter);
      } else if (variant === 'unassignCase') {
        casesActions.setPendingFilterUnassignCase(updatedFilter);
      } else if (variant === 'resolvedCase') {
        casesActions.setPendingFilterResolvedCase(updatedFilter);
      }
    },
    [variant, pendingFilter, casesActions]
  );

  /**
   * Apply pending filter to active filter
   */
  const handleApplyFilter = useCallback(() => {
    // Use the pendingFilter value directly to ensure we apply exactly what's in the hook
    if (variant === 'activeCase') {
      casesActions.setFilterActiveCase(pendingFilter);
    } else if (variant === 'unassignCase') {
      casesActions.setFilterUnassignCase(pendingFilter);
    } else if (variant === 'resolvedCase') {
      casesActions.setFilterResolvedCase(pendingFilter);
    }
  }, [variant, pendingFilter, casesActions]);

  /**
   * Reset pending filter to match current active filter
   */
  const handleResetFilter = useCallback(() => {
    if (variant === 'activeCase') {
      casesActions.resetPendingFilterActiveCase();
    } else if (variant === 'unassignCase') {
      casesActions.resetPendingFilterUnassignCase();
    } else if (variant === 'resolvedCase') {
      casesActions.resetPendingFilterResolvedCase();
    }
  }, [variant, casesActions]);

  /**
   * Handle close modal
   */
  const handleClose = useCallback(() => {
    handleResetFilter();
    caseModuleModalRootActions.closeModal('FilterCaseModal');
  }, [caseModuleModalRootActions, handleResetFilter]);

  /**
   * Handle apply filter
   */
  const handleApply = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      handleDomBubbleEvent(e);
      handleApplyFilter();
      caseModuleModalRootActions.closeModal('FilterCaseModal');
    },
    [caseModuleModalRootActions, handleApplyFilter]
  );

  return (
    <Modal isOpen={isOpen} lockBodyScroll onClose={handleClose} isCloseOutside={false} className="p-[32px] w-[500px]">
      <ModalHeader>
        <ModalTitle>{filterModalTitle}</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="flex justify-center flex-col gap-[16px] ">
          <div className="space-y-[8px]">
            <div className="space-y-[4px] w-full">
              <label htmlFor="label-case" className="text-[14px] block font-normal text-[#9D9D9C]">
                Label
              </label>
              <MultiSelectDropdown<TLabelCase>
                options={labelCaseOptions}
                value={Array.isArray(selectedLabelCases) ? selectedLabelCases : []}
                onChange={(value) => {
                  handleFilterChange({ labelCases: value as typeof selectedLabelCases });
                }}
                placeholder="Select Label"
                searchPlaceholder="Search Label..."
                labelKey="name"
                valueKey="labelCaseId"
                isLoading={isLoadingLabelCase}
                isDisplaySelectedItem={true}
                limit={3}
              />
            </div>
            <div className="space-y-[4px] w-full">
              <label htmlFor="channel" className="text-[14px] block font-normal text-[#9D9D9C]">
                Channel
              </label>
              <GenericMultiselectDropdown
                id="channel"
                options={organizationChannelOptions}
                value={Array.isArray(selectedChannels) ? selectedChannels : []}
                onChange={(value) => {
                  handleFilterChange({ channels: value as typeof selectedChannels });
                }}
                placeholder="Select Channels"
                searchPlaceholder="Search channels..."
                labelKey="displayName"
                valueKey="organizationChannelId"
                isLoading={isLoadingOrganizationChannel}
                renderOption={(option) => {
                  return (
                    <div className="flex items-center gap-2">
                      <Avatar
                        imgSrc={''}
                        username={option.displayName}
                        className="w-[24px] h-[24px] rounded-full"
                        channel={option.channel?.name as unknown as TChannelOption}
                        classNamePlaceholder="text-[12px]"
                        channelClassName="text-[12px] bottom-[-4px] right-[-4px]"
                      />
                      <span className="text-[12px] font-normal text-[#4B4B4B]">{option.displayName}</span>
                    </div>
                  );
                }}
              />
            </div>
            <div className="space-y-[4px] w-full">
              <label htmlFor="agent" className="text-[14px] block font-normal text-[#9D9D9C]">
                Agent
              </label>
              <GenericMultiselectDropdown
                id="agent"
                options={agentOptions}
                value={Array.isArray(selectedAgents) ? selectedAgents : []}
                onChange={(value) => {
                  handleFilterChange({ agents: value as typeof selectedAgents });
                }}
                placeholder="Select Agents"
                searchPlaceholder="Search agents..."
                labelKey="fullname"
                valueKey="userId"
                isLoading={isLoadingAgent}
              />
            </div>
            <div className="space-y-[4px] w-full">
              <label htmlFor="team" className="text-[14px] block font-normal text-[#9D9D9C]">
                Teams
              </label>
              <GenericMultiselectDropdown
                id="team"
                options={teamOptions}
                value={Array.isArray(selectedTeams) ? selectedTeams : []}
                onChange={(value) => {
                  handleFilterChange({ teams: value as typeof selectedTeams });
                }}
                placeholder="Select Teams"
                searchPlaceholder="Search teams..."
                labelKey="name"
                valueKey="teamId"
                isLoading={isLoadingTeam}
              />
            </div>
            <div className="space-y-[4px]">
              <label htmlFor="search-customer-or-group" className="text-[14px] font-normal text-[#9D9D9C]">
                Search
              </label>
              <Input
                id="search-customer-or-group"
                type="text"
                placeholder="Search customer or group..."
                value={searchCustomerOrGroupName || ''}
                onChange={(e) => {
                  handleFilterChange({ searchCustomerOrGroupName: e.target.value });
                }}
              />
            </div>
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleApply(e);
          }}
        >
          Done
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
FilterCaseModal.displayName = 'FilterCaseModal';

export default FilterCaseModal;
